import type { Toast, ToastOptions } from '@ts-toast/interfaces'
import type { ToastPayload } from '@ts-toast/types'

const toasts = ref<Toast[]>([])
const timers = new Map<string, ReturnType<typeof setTimeout>>()
const TOAST_LIMIT = 5

let counter = 0

const generateId = (): string => `toast-${++counter}-${Date.now()}`

interface ToastReturn {
  toasts: Ref<readonly Toast[]>
  add: (payload: ToastPayload) => string
  dismiss: (id: string) => void
  dismissAll: () => void
  success: (descriptionOrOptions: string | ToastOptions) => string
  error: (descriptionOrOptions: string | ToastOptions) => string
  warning: (descriptionOrOptions: string | ToastOptions) => string
  info: (descriptionOrOptions: string | ToastOptions) => string
}

export const useToast = (): ToastReturn => {

  const add = (payload: ToastPayload): string => {
    const id = generateId()

    const toast: Toast = {
      id,
      duration: 3000,
      closable: true,
      progress: true,
      ...payload,
    }

    toasts.value.push(toast)

    if (toasts.value.length > TOAST_LIMIT) {
      toasts.value.splice(0, toasts.value.length - TOAST_LIMIT)
    }

    if (toast.duration && toast.duration > 0) {
      const timer = setTimeout(() => {
        timers.delete(id)
        dismiss(id)
      }, toast.duration)
      timers.set(id, timer)
    }

    return id
  }

  const dismiss = (id: string): void => {
    const timer = timers.get(id)
    if (timer) {
      clearTimeout(timer)
      timers.delete(id)
    }

    const index = toasts.value.findIndex((t) => t.id === id)
    if (index > -1) {
      toasts.value.splice(index, 1)
    }
  }

  const dismissAll = (): void => {
    timers.forEach((timer) => {
      clearTimeout(timer)
    })
    timers.clear()
    toasts.value.splice(0)
  }

  const resolveOptions = (descriptionOrOptions: string | ToastOptions): ToastOptions => {
    return typeof descriptionOrOptions === 'string'
      ? { description: descriptionOrOptions }
      : descriptionOrOptions
  }

  const success = (descriptionOrOptions: string | ToastOptions): string => add({ variant: 'success', ...resolveOptions(descriptionOrOptions) })
  const error = (descriptionOrOptions: string | ToastOptions): string => add({ variant: 'error', ...resolveOptions(descriptionOrOptions) })
  const warning = (descriptionOrOptions: string | ToastOptions): string => add({ variant: 'warning', ...resolveOptions(descriptionOrOptions) })
  const info = (descriptionOrOptions: string | ToastOptions): string => add({ variant: 'info', ...resolveOptions(descriptionOrOptions) })

  return {
    toasts: readonly(toasts),
    add,
    dismiss,
    dismissAll,
    success,
    error,
    warning,
    info,
  }
}
